import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { Button, Input, Radio, Typography, Form } from 'antd';
import { CancelSubscriptionForm, INITIAL_VALUES, REASONS } from './config';

type Props = {
  onCancel?: (...args: any[]) => any;
  setStep: Dispatch<SetStateAction<number>>;
  onOk?: (...args: any[]) => any;
}

const CancelReasonStep:FC<Props> = ({ onCancel, setStep, onOk }) => {
  const [form] = Form.useForm();
  const reason = Form.useWatch('reason', form);
  const reasonDetails = Form.useWatch('reasonDetails', form);

  useEffect(() => {
    form.setFieldValue("reasonDetails", "")
  }, [reason]);

  const handleNevermindClick = () => {
    onCancel?.()
    setStep(1)
  }

  const handleSubmit = async (values: CancelSubscriptionForm) => {
    const data = {
      reason: REASONS[values.reason].title,
      ...(values?.reasonDetails ? { reasonDetails: values?.reasonDetails } : {}),
      ...(values?.followUp ? { followUp: values?.followUp } : {}),
      ...(values?.contactInfo ? { contactInfo: values?.contactInfo } : {}),
    }

    onOk?.(data);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={INITIAL_VALUES}
      onFinish={handleSubmit}
    >
      <Typography.Title level={2} style={{ color: "#111" }}>
        We're Sorry to See You Go!
      </Typography.Title>
      <Typography style={{ margin: "16px 0" }}>
        To help us improve, please tell us why you're leaving Mortgage Maker:
      </Typography>
      <div>
        <div>
          <Typography.Text strong>
            Reason for cancellation (select one):
          </Typography.Text>
          <Form.Item name="reason">
            <Radio.Group>
              {Object.entries(REASONS).map(([key, value]) => (
                <Radio key={key} value={key}>
                  <div className="flex-col gap-4 p-block-10">
                    <Typography.Text className="fw-500">{value.title}</Typography.Text>
                    {value?.text && <Typography.Text className="color-gray">{value.text}</Typography.Text>}
                    {reason === key && reason === 'financialReasons' && (
                      <Form.Item name="reasonDetails">
                        <Radio.Group style={{ marginTop: 8 }}>
                          <Radio value="Yes, please contact me.">Yes, please contact me.</Radio>
                          <Radio value="No, my decision is final.">No, my decision is final.</Radio>
                        </Radio.Group>
                      </Form.Item>
                    )}
                    {reason === key && value?.hasTextField && (
                      <Form.Item name="reasonDetails">
                        <Input.TextArea
                          placeholder="Please specify..."
                          autoSize={{ minRows: 3, maxRows: 5 }}
                          style={{ marginTop: 8 }}
                        />
                      </Form.Item>
                    )}
                  </div>
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </div>

        <div>
          <Typography.Text strong>
            Follow-up (Optional):
          </Typography.Text>
          <Typography.Text style={{ display: "block", marginBottom: 2 }}>
            Would you be open to us contacting you for further feedback?
          </Typography.Text>
          <Form.Item name="followUp">
            <Radio.Group>
              <Radio value="Yes">Yes</Radio>
              <Radio value="No">No</Radio>
            </Radio.Group>
          </Form.Item>
        </div>

        <Form.Item
          name="contactInfo"
          label={<Typography.Text strong>Best Contact (Optional)</Typography.Text>}
        >
          <Input placeholder="Email or phone number" style={{ marginTop: 2 }} />
        </Form.Item>
      </div>
      <div className="flex-row flex-justify-end gap-12">
        <Button type="default" size="large" onClick={handleNevermindClick}>
          Nevermind I'll Stay!
        </Button>
        <Form.Item>
          <Button
            type="primary"
            danger
            size="large"
            htmlType="submit"
            disabled={!reasonDetails && reason !== "closingYourBusiness"}
          >
            Confirm Cancellation
          </Button>
        </Form.Item>
      </div>
    </Form>
  )
}

export default CancelReasonStep;
